"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { signOut, useSession } from "next-auth/react";

export type AuthUser = {
  email: string;
  name?: string;
};

export function useAuth() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const isPending = status === "loading";
  // Facebook/Apple özel oturumu Auth.js session'da görünmez — ayrı yoklanır.
  // Yalnızca Auth.js boşken tek istek (sonsuz döngü yok: bağımlılıklar stabil).
  const [oauthUser, setOauthUser] = useState<AuthUser | null>(null);
  useEffect(() => {
    if (isPending || session?.user?.email) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/oauth/session", { cache: "no-store" });
        const data = (await res.json().catch(() => null)) as { email?: unknown; name?: unknown } | null;
        if (cancelled) return;
        const email = typeof data?.email === "string" ? data.email : "";
        if (email.includes("@")) {
          const name = typeof data?.name === "string" ? data.name : undefined;
          setOauthUser({ email, ...(name ? { name } : {}) });
        }
      } catch {
        // sessiz — misafir sayılır
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPending, session?.user?.email]);
  // Stabil kimlik — her render'da yeni nesne dönerse consumer effect'ler sonsuz döngüye girer
  const user: AuthUser | null = useMemo(() => {
    const email = session?.user?.email;
    if (email) {
      const name = session?.user?.name ?? undefined;
      return { email, ...(name ? { name } : {}) };
    }
    return oauthUser;
  }, [session?.user?.email, session?.user?.name, oauthUser]);

  return {
    user,
    isAuthenticated: user !== null,
    isLoading: isPending,
    logout: async () => {
      try {
        await signOut({ redirect: false });
      } finally {
        // Özel OAuth çerezi varsa onu da düşür (best-effort)
        try {
          await fetch("/api/oauth/logout", { method: "POST" });
        } catch {
          /* yoksay */
        }
        router.push("/");
        router.refresh();
      }
    },
  };
}
